import { PRIORITY_LEVELS, REQUEST_STATUS } from '../constants/request.constant';

const ACTIVE_WORKFLOW_STATUSES = [
  REQUEST_STATUS.ASSIGNED,
  REQUEST_STATUS.PENDING,
  REQUEST_STATUS.IN_PROGRESS,
];

/** Global CSS suffix for list cards (Reviews_Cards/styles.css). */
export const PRIORITY_LIST_CARD_CLASS = {
  [PRIORITY_LEVELS.CRITICAL]: 'critical',
  [PRIORITY_LEVELS.HIGH]: 'high',
  [PRIORITY_LEVELS.MEDIUM]: 'normal',
  [PRIORITY_LEVELS.LOW]: 'low',
};

/** CSS module keys for RequestBadges.module.css. */
export const PRIORITY_MODULE_CLASS_KEY = {
  [PRIORITY_LEVELS.CRITICAL]: 'priorityBadgeCritical',
  [PRIORITY_LEVELS.HIGH]: 'priorityBadgeHigh',
  [PRIORITY_LEVELS.MEDIUM]: 'priorityBadgeMedium',
  [PRIORITY_LEVELS.LOW]: 'priorityBadgeLow',
};

export function normalizePriorityLevel(priority) {
  return String(priority || PRIORITY_LEVELS.LOW).toLowerCase();
}

export function getPriorityListCardClass(priority) {
  const normalized = normalizePriorityLevel(priority);
  return PRIORITY_LIST_CARD_CLASS[normalized] || 'normal';
}

export function getPriorityModuleClass(styles, priority) {
  const normalized = normalizePriorityLevel(priority);
  const classKey = PRIORITY_MODULE_CLASS_KEY[normalized];
  return (classKey && styles[classKey]) || styles.priorityBadge;
}

/**
 * Maps API priority level to shared CSS modifier class.
 */
export function getPriorityBadgeClass(priorityLevel) {
  switch ((priorityLevel || '').toLowerCase()) {
    case 'critical':
      return 'critical';
    case 'high':
      return 'high';
    case 'low':
      return 'low';
    case 'medium':
      return 'medium';
    default:
      return 'normal';
  }
}

/**
 * Full className string for a priority badge element.
 */
export function getPriorityBadgeClassName(priorityLevel) {
  return `priority-badge ${getPriorityBadgeClass(priorityLevel)}`;
}

/**
 * Whether a review card should show the priority badge instead of status.
 */
export function shouldDisplayPriorityBadge({
  status,
  priority,
  hasTag,
  isActiveWorkflowTab,
}) {
  const isApprovedOrRejected =
    status === REQUEST_STATUS.APPROVED || status === REQUEST_STATUS.REJECTED;
  return (
    isApprovedOrRejected ||
    (isActiveWorkflowTab && priority && priority !== 'low' && !hasTag)
  );
}

export function isActiveWorkflowStatus(status) {
  return ACTIVE_WORKFLOW_STATUSES.includes(status);
}

/**
 * Human-readable priority label; returns "-" when SLA/priority is unavailable.
 */
export function formatPriorityLabel(priority) {
  if (!priority) return '-';
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

/**
 * Resolve terminal outcome for completed requests (approved / rejected).
 * Returns REQUEST_STATUS.APPROVED, REQUEST_STATUS.REJECTED, or null.
 */
export function getRequestOutcomeStatus(dateEnded, requestsProgresses = []) {
  if (!dateEnded || !Array.isArray(requestsProgresses)) {
    return null;
  }

  const terminalProgress = requestsProgresses.find((progress) => {
    const approvalCode = progress?.StageRelation?.Stage?.ApprovalType?.code;
    return (
      approvalCode === REQUEST_STATUS.APPROVED ||
      approvalCode === REQUEST_STATUS.REJECTED
    );
  });

  const approvalCode =
    terminalProgress?.StageRelation?.Stage?.ApprovalType?.code ?? null;

  if (approvalCode === REQUEST_STATUS.APPROVED) {
    return REQUEST_STATUS.APPROVED;
  }
  if (approvalCode === REQUEST_STATUS.REJECTED) {
    return REQUEST_STATUS.REJECTED;
  }

  return null;
}
